import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

// Create Auth Context
const AuthContext = createContext();

// AuthProvider Component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSigningUp, setIsSigningUp] = useState(false); // Flag to prevent onAuthStateChanged overwrite

  const fetchUserData = async (user) => {
    if (!user || !user.uid) {
      return { role: 'unknown' };
    }
    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        return userDoc.data() || { role: 'unknown' };
      }
      return { role: 'unknown' };
    } catch (err) {
      console.error('Failed to fetch user role:', err.message, err);
      return { role: 'unknown' };
    }
  };

  const setUserRole = async (user) => {
    if (user && user.uid) {
      setIsSigningUp(true);
      let userData = await fetchUserData(user);
      let attempts = 0;
      const maxAttempts = 5;
      const delay = 500;

      while (userData.role !== 'practitioner' && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, delay));
        userData = await fetchUserData(user);
        attempts++;
      }

      setCurrentUser({
        ...user,
        role: userData.role,
        firstName: userData.firstName,
        lastName: userData.lastName
      });
      setIsSigningUp(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !isSigningUp) {
        const userData = await fetchUserData(user);
        setCurrentUser({
          ...user,
          role: userData.role,
          firstName: userData.firstName,
          lastName: userData.lastName
        });
      } else if (!user) {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isSigningUp]);

  return (
    <AuthContext.Provider value={{ currentUser, fetchUserData, setUserRole, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Hook to access Auth Context
export const useAuth = () => useContext(AuthContext);